// The Job Queue's reads: ONE copy, used by the desktop Job Queue (pages/JobQueuePage.jsx) and the
// phone queue (mobile/screens/QueueScreen.jsx). docs/mobile-pwa-plan.md "One copy of every action":
// reads are shared the same way as the actions (./taskActions.js). Plain async functions, no React:
// each screen keeps its own state, its own alerts (job note, flagged parts) and its own timers.
import config from "../../config.js";
import { apiErrorMessage } from "../../utils/apiError";
import { filterLookupRows } from "../../utils/queueSearch";

const API_BASE = config.API_BASE_URL;

/** The stations a queue can be opened for, in the order the shop lists them (one list, kept with
 *  the scan-link rules in utils/scanCodes.js). */
export { QUEUE_STATIONS } from "../../utils/scanCodes";

/** A queue row's status (rows carry task_status; older ones only status). */
export function rowStatus(row) {
  return (row && (row.task_status || row.status)) || "";
}

/** GET /queue/{station}: the station's rows (Pending work due or scheduled within the window, plus
 *  everything started). Throws Error(the API's sentence). */
export async function fetchQueueRows(station, { includeCompleted = false } = {}) {
  let url = `${API_BASE}/queue/${station}`;
  if (includeCompleted) url += "?include_completed=true";
  const res = await fetch(url);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

/** The open row that locks the station (critical maintenance: block_production), or null. */
export function stationBlocker(rows) {
  if (!Array.isArray(rows)) return null;
  return rows.find((t) => t.block_production === true && ["Pending", "In Progress", "Paused"].includes(rowStatus(t))) || null;
}

/** One row per task, first copy kept (non-Laser lists are one item per task). */
export function uniqueTasks(rows) {
  const seen = new Map();
  (Array.isArray(rows) ? rows : []).forEach((item) => {
    if (item.task_id && !seen.has(item.task_id)) seen.set(item.task_id, item);
  });
  return Array.from(seen.values());
}

/**
 * Beyond-window lookup: GET /queue/{station}?find=n (no date horizon) for a job or task number the
 * loaded queue doesn't hold. Resolves {rows, unsupported}: rows = only job/task n; unsupported = an
 * API without `find` answered with its ordinary queue, which says nothing about whether n exists.
 * Throws Error(sentence) on a failed request.
 */
export async function findInQueue(station, n, { includeCompleted = false } = {}) {
  let url = `${API_BASE}/queue/${station}?find=${n}`;
  if (includeCompleted) url += "&include_completed=true";
  const res = await fetch(url);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  const data = await res.json();
  const rows = filterLookupRows(data, n);
  return { rows, unsupported: Array.isArray(data) && data.length > 0 && rows.length === 0 };
}

/**
 * A job as a station sees it: GET /jobs/{id} + GET /jobs/{id}/progress. The Laser sees every task of
 * the job; any other station only the selected task. `preShippedQty` comes from the queue row (GET
 * /jobs/{id} doesn't carry it, and it is per task). Resolves {detail, job, allTasks}; throws
 * Error(sentence) when either request fails.
 */
export async function loadJobDetail({ jobId, taskId, workstation, preShippedQty = 0 }) {
  const detailRes = await fetch(`${API_BASE}/jobs/${jobId}`);
  if (!detailRes.ok) throw new Error(await apiErrorMessage(detailRes));
  const job = await detailRes.json();

  const progRes = await fetch(`${API_BASE}/jobs/${jobId}/progress`);
  if (!progRes.ok) throw new Error(await apiErrorMessage(progRes));
  const progData = await progRes.json();
  const allTasks = progData.tasks || [];

  const tasks = workstation === "Laser" ? allTasks : allTasks.filter((t) => t.task_id === taskId);
  const detail = {
    ...job,
    pre_shipped_qty: preShippedQty ?? 0,
    tasks,
    incoming_remakes: progData.incoming_remakes || [],
    decoupled_defects: progData.decoupled_defects || [],
  };
  return { detail, job, allTasks };
}

/**
 * GET /jobs/{id}/parts, each part filled in from GET /parts/{id} (image, flag, assemblies, PPAP,
 * blast times, coating colour). This allowlist is the ONLY path part fields take to a station: a
 * field not copied here is dropped. null when the parts list itself can't be read.
 */
export async function loadJobParts(jobId) {
  const partsRes = await fetch(`${API_BASE}/jobs/${jobId}/parts`);
  if (!partsRes.ok) return null;
  const partArr = await partsRes.json();
  for (const p of partArr) {
    try {
      const pRes = await fetch(`${API_BASE}/parts/${p.part_id}`);
      if (pRes.ok) {
        const pData = await pRes.json();
        p.image_path = pData.image_path || null;
        p.flagged = !!pData.flagged;
        p.flag_reason = pData.flag_reason || "";
        p.flagged_by_name = pData.flagged_by_name || "";
        p.flagged_at = pData.flagged_at || "";
        p.assemblies = pData.assemblies || [];
        p.ppap_required = pData.ppap_required || false;
        p.ppap_sample_size_ongoing = pData.ppap_sample_size_ongoing || 3;
        p.blast_time_top = pData.blast_time_top || "";
        p.blast_time_bottom = pData.blast_time_bottom || "";
        // Requested finish colour, shown at Coating so the operator can check it against the PO.
        p.coating_color_code = pData.coating_color_code || "";
        p.coating_color_name = pData.coating_color_name || "";
        p.coating_color_hex = pData.coating_color_hex || "";
      }
    } catch (innerErr) {
      console.log("Error fetching part detail:", innerErr);
    }
  }
  return partArr;
}

/** GET /cut_sheets/{id}, each part filled in from GET /parts/{id} (image, PPAP). null when the cut
 *  sheet can't be read. */
export async function loadCutSheet(cutSheetId) {
  const csRes = await fetch(`${API_BASE}/cut_sheets/${cutSheetId}`);
  if (!csRes.ok) return null;
  const csData = await csRes.json();
  if (csData.parts && csData.parts.length > 0) {
    for (const cp of csData.parts) {
      try {
        const cpRes = await fetch(`${API_BASE}/parts/${cp.part_id}`);
        if (cpRes.ok) {
          const cpData = await cpRes.json();
          cp.image_path = cpData.image_path || null;
          cp.ppap_required = cpData.ppap_required || false;
          cp.ppap_sample_size_ongoing = cpData.ppap_sample_size_ongoing || 3;
          cp.ppap_sample_size_initial = cpData.ppap_sample_size_initial || 0;
          cp.ppap_critical_characteristics = cpData.ppap_critical_characteristics || [];
          cp.ppap_image_path = cpData.ppap_image_path || null;
        }
      } catch (errFetchPart) {
        console.log("Error fetching part detail for cut sheet part:", errFetchPart);
      }
    }
  }
  return csData;
}

/** GET /jobs/{id}/activity_log, only the selected task's entries. null when it can't be read. */
export async function loadTaskActivity(jobId, taskId) {
  const logRes = await fetch(`${API_BASE}/jobs/${jobId}/activity_log`);
  if (!logRes.ok) return null;
  const logData = await logRes.json();
  return logData.filter((entry) => entry.task_id === taskId);
}

/** GET /defects?job_id=: the job's defects, [] when they can't be read (logged, never thrown). */
export async function loadJobDefects(jobId) {
  try {
    const res = await fetch(`${API_BASE}/defects?job_id=${jobId}`);
    if (!res.ok) {
      console.error(`Error fetching defects: HTTP ${res.status}`);
      return [];
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("Error fetching defects:", err);
    return [];
  }
}

/** GET /stations/laser/consumable_status: nozzle / lens / ... life vs average. null when it can't be
 *  read (logged, never thrown). */
export async function loadLaserConsumables() {
  try {
    const res = await fetch(`${API_BASE}/stations/laser/consumable_status`);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error("Failed to load laser metrics", err);
    return null;
  }
}
